import { Transaction } from "@firebase/firestore";
import { Unsubscribe } from "@firebase/util";
import { IBillingData } from "api/interfaces/megaERP";
import { markFieldToDelete } from "api/repositories/FirebaseRepository";
import LoggerRepository from "api/repositories/LoggerRepository";
import axios from "axios";
import {
  validateNotNegativeNumbers,
  validateRequiredField,
} from "utils/ValidationsUtils";
import { removeEmptyFields } from "../../utils";
import { decimal } from "../../utils/DecimalPrecision";
import { EDiscountType, EMeasurementStatus, EOrderStatus } from "../enums";
import { RequiredParamException } from "../exceptions/InvalidParamException";
import { GreaterThanOrEqualException } from "../exceptions/InvalidValueException";
import { MeasurementBilledException } from "../exceptions/MeasurementException";
import {
  IAdvanceLink,
  IBillingOrder,
  IDiscountLink,
  IMeasurementTotalsPerMonthData,
} from "../interfaces";
import ContractModel from "../models/ContractModel";
import MeasurementModel from "../models/MeasurementModel";
import MeasurementRepository from "../repositories/MeasurementRepository";

class MeasurementService {
  public async createMeasurement(
    measurement: MeasurementModel
  ): Promise<string> {
    this.validateMeasurement(measurement);
    return MeasurementRepository.addMeasurementToDataBase(measurement);
  }

  public async deleteMeasurement(measurement: MeasurementModel): Promise<void> {
    if (measurement.isBilled()) throw new MeasurementBilledException();

    return MeasurementRepository.deleteMeasurementFromDataBase(
      measurement.contractUid,
      measurement.uid
    );
  }

  public async updateMeasurementDescription(
    measurement: MeasurementModel,
    description: string
  ): Promise<void> {
    if (!measurement.canEdit()) throw new MeasurementBilledException();
    validateRequiredField("description", description);

    return MeasurementRepository.updateMeasurementOnDataBase(
      measurement.contractUid,
      measurement.uid,
      {
        description,
      }
    );
  }

  public async updateMeasurementTotal(
    measurement: MeasurementModel,
    total: number
  ): Promise<void> {
    if (!measurement.canEdit()) throw new MeasurementBilledException();
    validateNotNegativeNumbers("total", total);

    return MeasurementRepository.updateMeasurementOnDataBase(
      measurement.contractUid,
      measurement.uid,
      { total }
    );
  }

  public async updateMeasurementStatus(
    measurement: MeasurementModel,
    status: EMeasurementStatus
  ): Promise<void> {
    if (measurement.isBilled()) throw new MeasurementBilledException();
    validateRequiredField("status", status);

    return MeasurementRepository.updateMeasurementOnDataBase(
      measurement.contractUid,
      measurement.uid,
      { status }
    );
  }

  public async updateMeasurementPeriod(
    measurement: MeasurementModel,
    initialDate: Date,
    finalDate: Date
  ): Promise<void> {
    if (!measurement.canEdit()) throw new MeasurementBilledException();
    this.validatePeriodDates(initialDate, finalDate);

    return MeasurementRepository.updateMeasurementOnDataBase(
      measurement.contractUid,
      measurement.uid,
      {
        initialDate,
        finalDate,
      }
    );
  }

  /** Atualiza o valor do reajuste da medição no banco de dados, verificando
   * se a medição ainda pode ser editada.
   * @param {MeasurementModel} measurement Model da medição que será atualizada
   * @param {number} readjustValue Novo valor do reajuste
   * @throws {MeasurementBilledException} Medição não pode ser editada
   * @returns {Promise<void>}
   */
  public async updateMeasurementReadjustment(
    measurement: MeasurementModel,
    readjustValue: number
  ): Promise<void> {
    if (!measurement.canEdit()) throw new MeasurementBilledException();

    return MeasurementRepository.updateMeasurementOnDataBase(
      measurement.contractUid,
      measurement.uid,
      {
        readjustValue,
      }
    );
  }

  public async updateMeasurementAdvancesWithTransaction(
    transaction,
    measurement: MeasurementModel,
    advances: IAdvanceLink[]
  ): Promise<void> {
    if (!measurement.canEdit()) throw new MeasurementBilledException();
    this.validateNetTotal(measurement, { advances });

    if (!advances) advances = markFieldToDelete();

    return MeasurementRepository.updateMeasurementOnDataBase(
      measurement.contractUid,
      measurement.uid,
      { advances },
      transaction
    );
  }

  public async updateMeasurementDiscountsWithTransaction(
    transaction,
    measurement: MeasurementModel,
    discounts: IDiscountLink[]
  ): Promise<void> {
    if (!measurement.canEdit()) throw new MeasurementBilledException();
    this.validateNetTotal(measurement, { discounts });

    if (!discounts) discounts = markFieldToDelete();

    return MeasurementRepository.updateMeasurementOnDataBase(
      measurement.contractUid,
      measurement.uid,
      { discounts },
      transaction
    );
  }

  public async updateMeasurementBillingsWithTransaction(
    transaction,
    measurement: MeasurementModel,
    billings: IDiscountLink[]
  ): Promise<void> {
    if (!measurement.canEdit()) throw new MeasurementBilledException();
    this.validateNetTotal(measurement, { billings });

    if (!billings) billings = markFieldToDelete();

    return MeasurementRepository.updateMeasurementOnDataBase(
      measurement.contractUid,
      measurement.uid,
      { billings },
      transaction
    );
  }

  public async updateMeasurementDiscountsType(
    measurement: MeasurementModel,
    discountId: string,
    type: EDiscountType
  ): Promise<void> {
    const discountsFounded = [
      ...measurement.discounts,
      ...measurement.billings,
    ].filter((d) => d.discountId === discountId);
    if (!discountsFounded.length) return;

    const newDiscounts = measurement.discounts.filter(
      (d) => d.discountId !== discountId
    );
    const newBillings = measurement.billings.filter(
      (d) => d.discountId !== discountId
    );

    if (type === EDiscountType.BILLING) {
      newBillings.push(...discountsFounded);
    } else {
      newDiscounts.push(...discountsFounded);
    }

    return MeasurementRepository.updateMeasurementOnDataBase(
      measurement.contractUid,
      measurement.uid,
      { discounts: newDiscounts, billings: newBillings }
    );
  }

  /** Atualiza o documento da medição com os dados
   * do pedido enviado para geração do documento de
   * venda e passa seu status para 'Faturada'.
   */
  public async updateMeasurementToBilling(
    measurement: MeasurementModel,
    branchId: number,
    sequencial: number,
    issueDate: Date
  ): Promise<void> {
    if (!measurement.canEdit()) throw new MeasurementBilledException();
    validateRequiredField("sequencial", sequencial);
    validateRequiredField("branch", branchId);
    validateRequiredField("issueDate", issueDate);

    return MeasurementRepository.updateMeasurementOnDataBase(
      measurement.contractUid,
      measurement.uid,
      {
        status: EMeasurementStatus.FATURADA,
        billingOrder: {
          sequencial,
          branchId,
          issueDate,
        } as any,
      }
    );
  }

  /** Obtém os dados de envio para faturamento da medição. */
  public async getMeasurementBillingData(
    measurement: MeasurementModel
  ): Promise<IBillingData | undefined> {
    if (!measurement) return;

    return MeasurementRepository.getMeasurementBillingDataFromDataBase(
      measurement.contractUid,
      measurement.uid
    );
  }

  /** Atualiza o documento da medição para salvar os
   * dados enviados para faturamento desta medição.
   */
  public async updateMeasurementBillingData(
    measurement: MeasurementModel,
    dataSent: IBillingData
  ): Promise<void> {
    if (!measurement || !dataSent) return;

    await MeasurementRepository.updateMeasurementBillingDataOnDataBase(
      measurement.contractUid,
      measurement.uid,
      dataSent
    );
    return MeasurementRepository.updateMeasurementOnDataBase(
      measurement.contractUid,
      measurement.uid,
      {
        hasBillingData: true,
      }
    );
  }

  public async updateBillingOrderToDeleted(
    measurement: MeasurementModel
  ): Promise<void> {
    const billingOrder = measurement.billingOrder;
    return this.updateMeasurementBillingOrder(measurement, {
      ...billingOrder,
      status: EOrderStatus.DELETED,
    });
  }

  public async updateMeasurementBillingOrder(
    measurement: MeasurementModel,
    billingOrder: IBillingOrder
  ): Promise<void> {
    validateRequiredField("sequencial", billingOrder.sequencial);
    validateRequiredField("branch", billingOrder.branchId);

    billingOrder = removeEmptyFields(billingOrder, true);
    const hasError = ["canceled", "deleted", "error"].includes(
      billingOrder.status
    );

    if (hasError)
      return MeasurementRepository.updateMeasurementOnDataBase(
        measurement.contractUid,
        measurement.uid,
        {
          status: EMeasurementStatus.ANDAMENTO,
          billingOrder,
        }
      );

    return MeasurementRepository.updateMeasurementOnDataBase(
      measurement.contractUid,
      measurement.uid,
      { billingOrder }
    );
  }

  public async updateMeasurementAttachments(
    measurement: MeasurementModel,
    attachments: string[],
    transaction: Transaction
  ): Promise<void> {
    return MeasurementRepository.updateMeasurementOnDataBase(
      measurement.contractUid,
      measurement.uid,
      { attachments },
      transaction
    );
  }

  public async getNextCode(contractUid: string): Promise<number> {
    const lastCode = await MeasurementRepository.getLastCodeOfMeasurements(
      contractUid
    );
    return lastCode + 1;
  }

  public async getBillingTotals(contractUid: string): Promise<number> {
    const measurements =
      await MeasurementRepository.getAllMeasurementsFromDataBase(contractUid);

    return measurements.reduce((sum, measurement) => {
      if (measurement.status === EMeasurementStatus.FATURADA)
        sum = decimal(sum).sum(measurement.total).toNumber();
      return sum;
    }, 0);
  }

  public async countMeasurements(contractUid: string): Promise<number> {
    const measurements =
      await MeasurementRepository.getAllMeasurementsFromDataBase(contractUid);
    return measurements.length;
  }

  /**
   * Cria um log no Firestore com os dados de valores divergentes
   * da medição.
   * @param {MeasurementModel} measurement Medição com total incorreto
   * @param {number} expectedTotal Total experado para a medição
   */
  public async addLogOfInvalidMeasurementTotal(
    measurement: MeasurementModel,
    expectedTotal: number
  ) {
    if (!measurement) return;

    await LoggerRepository.createLogOnDataBase(
      `Total da medição divergente. [Valor esperado=${expectedTotal}] [Valor atual=${measurement.total}]`,
      {
        contractUid: measurement.contractUid,
        measurementUid: measurement.uid,
      }
    );
  }

  /** Chama a API responsável por calcular os valores
   * acumulados das linhas de medições até a medição
   * atual.
   */
  public async fetchCalculateAccumulatedUntilMeasurement(
    contractUid: string,
    code: number
  ): Promise<{
    total: number;
    wbs: { uid: string; quantity: number; total: number }[];
  }> {
    const data = {
      contractUid,
      code,
    };

    return axios
      .post(`${process.env.REACT_APP_CALCULATE_ACCUMULATED_MEASUREMENT}`, data)
      .then((result) => {
        return {
          total: result.data.total || 0,
          wbs: result.data.wbs || [],
        };
      })
      .catch(() => ({
        total: 0,
        wbs: [],
      }));
  }

  public async fetchMeasurementsTotalsPerMonth(
    contractsUids: string[]
  ): Promise<IMeasurementTotalsPerMonthData> {
    const data = { contractsUids, lang: "pt-br" };
    const response = await axios.post(
      `${process.env.REACT_APP_GET_MEASUREMENTS_PER_MONTH}`,
      data
    );
    return response.data;
  }

  public subscribeMeasurementsSnapshot(
    contract: ContractModel,
    onCreate: (data: MeasurementModel[]) => void,
    onChange: (data: MeasurementModel[]) => void,
    onRemove: (data: MeasurementModel[]) => void
  ): Unsubscribe {
    const prepareMeasurements = (data: MeasurementModel[]) => {
      return data.map((measurement) => {
        measurement.attachContract(contract);
        return measurement;
      });
    };

    return MeasurementRepository.addMeasurementCollectionSnapshot(
      contract.uid,
      (data) => {
        onCreate(prepareMeasurements(data));
      },
      (data) => {
        onChange(prepareMeasurements(data));
      },
      onRemove
    );
  }

  public subscribeMeasurementSnapshot(
    contract: ContractModel,
    uid: string,
    onCreate: Function,
    onChange: Function
  ): Unsubscribe {
    return MeasurementRepository.addMeasurementDocumentSnapshot(
      contract.uid,
      uid,
      (data) => {
        if (data) data.attachContract(contract);
        onCreate(data);
      },
      (data) => {
        if (data) data.attachContract(contract);
        onChange(data);
      }
    );
  }

  private validateMeasurement(measurement: MeasurementModel) {
    validateRequiredField("description", measurement.description);
    validateRequiredField("code", measurement.code);
    validateRequiredField("status", measurement.status);
    validateNotNegativeNumbers("total", measurement.total);
    this.validatePeriodDates(measurement.initialDate, measurement.finalDate);
  }

  private validatePeriodDates(initialDate, finalDate) {
    if (!initialDate) throw new RequiredParamException("initialDate");
    if (!finalDate) throw new RequiredParamException("finalDate");

    if (finalDate < initialDate)
      throw new GreaterThanOrEqualException(
        finalDate,
        initialDate,
        "finalDate"
      );
  }

  private validateNetTotal(measurement: MeasurementModel, links) {
    const copyMeasurement = MeasurementModel.create(measurement.uid, {
      ...measurement,
      advances: measurement.advances,
      discounts: measurement.discounts,
      billings: measurement.billings,
      ...links,
    });
    const contract = measurement.getContract();
    if (contract) copyMeasurement.attachContract(contract);

    if (copyMeasurement.getNetTotal() < 0)
      throw new GreaterThanOrEqualException(
        copyMeasurement.getNetTotal(),
        0,
        "netTotal"
      );
  }
}

export default new MeasurementService();
